import React, {useEffect, useState} from 'react';
import {Avatar, Button, Form, Input, message, Modal, Select, Space, Switch, Table} from 'antd';
import {EditOutlined, PlusOutlined} from '@ant-design/icons';
import {createUser, fetchUsers} from "../api/users";
import {useAppSelector} from '../hooks';
import {User} from "../interfaces/User";

type UsersModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

export default function UsersModal({isOpen, onClose}: UsersModalProps) {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [addUserVisible, setAddUserVisible] = useState<boolean>(false);
    const [form] = Form.useForm();
    const theme = useAppSelector(state => state.theme.value); // 'light' ou 'dark'

    useEffect(() => {
        setUsers([]); // Réinitialiser les utilisateurs à chaque ouverture du modal
        if (isOpen) {
            void loadUsers();
        }
    }, [isOpen]);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const data = await fetchUsers();
            setUsers(data);
        } catch (error) {
            console.error('Erreur lors de la récupération des utilisateurs:', error);
            message.error(`Impossible de charger les utilisateurs ${error}`);
        } finally {
            setLoading(false);
        }
    };

    const submitNewUser = async () => {
        try {
            const values = await form.validateFields();
            const createdUser = await createUser(values);
            setUsers((prev) => [...prev, createdUser]);
            message.success('Utilisateur créé');
            setAddUserVisible(false);
            form.resetFields();
        } catch (error) {
            console.error('Erreur lors de la création:', error);
            message.error('Erreur lors de la création');
        }
    };

    const columns = [
        {
            title: 'Nom',
            key: 'name',
            sorter: (a: User, b: User) => {
                const nameA = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim().toLowerCase();
                const nameB = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim().toLowerCase();
                return nameA.localeCompare(nameB);
            },
            render: (_: any, record: User) => (
                <Space>
                    <Avatar>
                        {`${record.firstName?.[0] || ''}${record.lastName?.[0] || ''}`.toUpperCase()}
                    </Avatar>
                    {record.firstName}{record.lastName}
                </Space>
            ),
        }, {
            title: "Nom d'utilisateur",
            dataIndex: 'username',
            key: 'username',
        },
        {
            title: 'E-mail',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Administrateur',
            dataIndex: 'isAdmin',
            key: 'isAdmin',
            render: (_: any, record: User) => (
                <Switch disabled={true} checked={record.isAdmin}/>
            ),
        },
        {
            title: '',
            key: 'actions',
            render: () => <Button type="text" icon={<EditOutlined/>}/>,
        },
    ];

    return (
        <div className={`g-root_theme_${theme}`}>
            <Modal
                title="Utilisateurs"
                centered={true}
                open={isOpen}
                onCancel={onClose}
                width="90%"
                footer={[
                    <Button key="add" type="primary" icon={<PlusOutlined/>} onClick={() => setAddUserVisible(true)}>
                        Ajouter un utilisateur
                    </Button>,
                ]}
                getContainer={false}
            >
                <Table
                    rowKey="idt"
                    dataSource={users}
                    columns={columns}
                    pagination={false}
                    scroll={{x: true, y: 300}}
                    loading={loading}
                />
            </Modal>

            {/* Modal pour ajouter un utilisateur */}
            <Modal
                title="Ajouter un utilisateur"
                open={addUserVisible}
                onCancel={() => setAddUserVisible(false)}
                onOk={submitNewUser}
                okText="Créer"
                cancelText="Annuler"
                getContainer={false}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="firstName" label="Prénom" rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="name" label="Nom" rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="username" label="Nom d'utilisateur" rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="email" label="Email" rules={[{required: true, type: 'email'}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="role" label="Rôle" rules={[{required: true}]}>
                        <Select>
                            <Select.Option value="USER">Utilisateur</Select.Option>
                            <Select.Option value="ADMIN">Administrateur</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
